import React, { useState, useEffect } from "react";
import { submitFeedback } from "../../utils/api/feedbackApi.js";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext.jsx";
import { Send, CheckCircle, User, Mail, Phone, MessageSquare, Tag, FileText, ArrowLeft } from "lucide-react";

const CATEGORIES = {
  ne: [
    "सामान्य सुझाव तथा प्रतिक्रिया",
    "शहरी योजना तथा भौतिक पूर्वाधार",
    "सुरक्षा तथा सार्वजनिक सुरक्षा",
    "खानेपानी तथा जल व्यवस्थापन",
    "फोहोर तथा प्रदूषण व्यवस्थापन",
    "वन, वातावरण तथा पर्यटन",
    "शिक्षा, अनुसन्धान तथा ज्ञान विकास",
    "शिल्प, सीप तथा उद्यमशीलता विकास",
    "स्वास्थ्य तथा जनस्वास्थ्य",
    "स्वस्थ जीवनशैली तथा उपभोक्ता हित संरक्षण",
    "खेलकुद तथा खेल प्रशिक्षण",
    "सूचना प्रविधि तथा स्मार्ट सिटी विकास",
    "मिडिया तथा सञ्चार",
    "व्यापार, उद्योग तथा आर्थिक विकास",
    "कृषि तथा सहकारी विकास",
    "विपद् व्यवस्थापन तथा जलवायु अनुकूलन",
    "कला, संस्कृति तथा पहिचान संरक्षण",
    "सुशासन, आर्थिक नीति तथा वित्तीय व्यवस्थापन",
    "सामाजिक सुरक्षा तथा समावेशी विकास",
    "महिला, बालबालिका तथा युवा विकास",
    "अन्तर्राष्ट्रिय सम्बन्ध तथा साझेदारी",
    "प्रशासन तथा संस्थागत व्यवस्थापन",
    "कार्यक्रम प्रतिक्रिया",
    "वेबसाइट / प्राविधिक समस्या",
    "गुनासो",
    "अन्य",
  ],
  en: [
    "General Suggestions & Feedback",
    "Urban Planning & Physical Infrastructure",
    "Security & Public Safety",
    "Water Supply & Water Management",
    "Waste & Pollution Management",
    "Forest, Environment & Tourism",
    "Education, Research & Knowledge Development",
    "Craft, Skills & Entrepreneurship Development",
    "Health & Public Health",
    "Healthy Lifestyle & Consumer Rights Protection",
    "Sports & Athletic Training",
    "Information Technology & Smart City Development",
    "Media & Communication",
    "Business, Industry & Economic Development",
    "Agriculture & Cooperative Development",
    "Disaster Management & Climate Adaptation",
    "Art, Culture & Identity Preservation",
    "Good Governance, Economic Policy & Financial Management",
    "Social Security & Inclusive Development",
    "Women, Children & Youth Development",
    "International Relations & Partnerships",
    "Administration & Institutional Management",
    "Program / Event Feedback",
    "Website / Technical Issue",
    "Complaint",
    "Other",
  ],
};

const CONTENT = {
  ne: {
    successTitle: "तपाईंको सुझाव तथा प्रतिक्रिया सफलतापूर्वक पठाइएको छ। धन्यवाद!",
    successDesc: "हामीलाई सुधार गर्न मद्दत गर्नुभएकोमा धन्यवाद। हाम्रो टोलीले तपाईंको सुझाव तथा प्रतिक्रिया छिट्टै समीक्षा गर्नेछ।",
    submitMore: "थप सुझाव पेश गर्नुहोस्",
    backToDashboard: "ड्यासबोर्डमा फर्कनुहोस्",
    backToHome: "गृहपृष्ठमा फर्कनुहोस्",
    eyebrow: "नागरिक सहभागिता",
    titleHighlight: "सुझाव",
    titleSuffix: "र प्रतिक्रिया",
    headerDesc1: "भोलिको इटहरी निर्माणका लागि तपाईंको मत, विचार र रचनात्मक सुझाव महत्वपूर्ण छ।",
    headerDesc2: "फारम भर्न २ मिनेट भन्दा कम समय लाग्छ।",
    formTitle: "सुझाव र प्रतिक्रिया फारम",
    formSubtitle: "आफ्नो सुझाव वा प्रतिक्रिया पठाउन तलको विवरण भर्नुहोस्",
    fullName: "पूरा नाम *",
    namePlaceholder: "तपाईंको पूरा नाम",
    email: "इमेल ठेगाना *",
    emailPlaceholder: "तपाईंको इमेल (उदा: ram@example.com)",
    phone: "फोन नम्बर",
    phonePlaceholder: "+९७७ ९८XXXXXXXX",
    categoryLabel: "सुझाव / प्रतिक्रियाको क्षेत्र वा प्रकार *",
    categoryPlaceholder: "क्षेत्र वा वर्ग छान्नुहोस्",
    subjectLabel: "विषय (ऐच्छिक)",
    subjectPlaceholder: "सुझावको मुख्य विषय",
    messageLabel: "तपाईंको सुझाव वा प्रतिक्रिया *",
    messagePlaceholder: "सुधारका लागि तपाईंको विचार, सुझाव वा प्रतिक्रिया विस्तृत रूपमा लेख्नुहोस्...",
    submitting: "पेश गर्दैछ…",
    submit: "सुझाव तथा प्रतिक्रिया पेश गर्नुहोस्",
    errorDefault: "सुझाव पेश गर्न असफल भयो। फेरि प्रयास गर्नुहोस्।",
    errNameReq: "कृपया आफ्नो पूरा नाम लेख्नुहोस्।",
    errEmailReq: "कृपया सही इमेल ठेगाना लेख्नुहोस्।",
    errMessageReq: "कृपया आफ्नो सुझाव वा प्रतिक्रिया लेख्नुहोस्।",
  },
  en: {
    successTitle: "Your suggestion and feedback have been submitted successfully. Thank you!",
    successDesc: "Thank you for helping us improve. Our team will review your feedback soon.",
    submitMore: "Submit Another Suggestion",
    backToDashboard: "Back to Dashboard",
    backToHome: "Back to Home",
    eyebrow: "Civic Participation",
    titleHighlight: "Suggestions",
    titleSuffix: "& Feedback",
    headerDesc1: "Your voice, ideas, and constructive feedback are essential in building Bholiko Itahari.",
    headerDesc2: "Filling the form takes less than 2 minutes.",
    formTitle: "Suggestions & Feedback Form",
    formSubtitle: "Fill out the details below to submit your suggestions or feedback",
    fullName: "Full Name *",
    namePlaceholder: "Your full name",
    email: "Email Address *",
    emailPlaceholder: "Your email (e.g. john@example.com)",
    phone: "Phone Number",
    phonePlaceholder: "+977 98XXXXXXXX",
    categoryLabel: "Category / Sector *",
    categoryPlaceholder: "Choose category or sector",
    subjectLabel: "Subject (Optional)",
    subjectPlaceholder: "Main topic of your feedback",
    messageLabel: "Your Suggestion or Feedback *",
    messagePlaceholder: "Write your ideas, constructive suggestions or feedback in detail...",
    submitting: "Submitting…",
    submit: "Submit Suggestion & Feedback",
    errorDefault: "Failed to submit feedback. Please try again.",
    errNameReq: "Please provide your full name.",
    errEmailReq: "Please provide a valid email address.",
    errMessageReq: "Please write your suggestion or feedback.",
  },
};

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  category: "",
  subject: "",
  message: "",
};

function Field({ label, col2, icon: Icon, children }) {
  return (
    <div className={col2 ? "col-span-1 sm:col-span-2" : "col-span-1"}>
      <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={13} className="text-green-800" />}
        {label}
      </label>
      {children}
    </div>
  );
}

const FeedbackForm = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEditor = location.pathname.startsWith("/editor");

  const { language } = useLanguage();
  const t = CONTENT[language] || CONTENT.ne;
  const categories = CATEGORIES[language] || CATEGORIES.ne;

  const [formData, setFormData] = useState({ ...EMPTY });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill category / sector from URL query param if present
  useEffect(() => {
    const sectorParam = searchParams.get("sector") || searchParams.get("category");
    if (sectorParam) {
      setFormData((prev) => ({
        ...prev,
        category: prev.category || sectorParam,
        subject: prev.subject || (sectorParam ? `${sectorParam}` : ""),
      }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError(t.errNameReq);
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email.trim())) {
      setError(t.errEmailReq);
      return false;
    }
    if (!formData.message.trim()) {
      setError(t.errMessageReq);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setLoading(true);
    try {
      await submitFeedback({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        category: formData.category || "सामान्य सुझाव तथा प्रतिक्रिया",
        subject: formData.subject.trim(),
        message: formData.message.trim(),
      });
      setSuccess(true);
      setFormData({ ...EMPTY });
    } catch (err) {
      setError(err?.response?.data?.message || t.errorDefault);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = formData.name.trim() && formData.email.trim() && formData.message.trim();

  /* ── Success Screen ── */
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-20 font-sans">
        <div className="max-w-lg w-full text-center">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-white text-4xl mx-auto mb-6 shadow-lg animate-bounce"
            style={{ background: "rgb(21, 75, 43)" }}
          >
            ✓
          </div>
          <h2
            className="text-2xl lg:text-3xl font-extrabold text-gray-900 mb-3 leading-snug"
            style={{ fontFamily: "'Playfair Display',serif" }}
          >
            {t.successTitle}
          </h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-8 max-w-md mx-auto">
            {t.successDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setSuccess(false)}
              className="px-6 py-3 rounded-xl font-bold text-sm text-white shadow-md transition-all duration-300 hover:opacity-90 active:scale-95"
              style={{ background: "rgb(21, 75, 43)" }}
            >
              {t.submitMore}
            </button>
            <Link
              to={isEditor ? "/editor/dashboard" : "/"}
              className="px-6 py-3 rounded-xl border border-gray-300 font-bold text-sm text-gray-700 hover:bg-gray-100 transition-all duration-300"
            >
              {isEditor ? t.backToDashboard : t.backToHome}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  /* ── Form Screen ── */
  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Header Banner */}
      <div className="pt-24 pb-14 px-6" style={{ background: "rgb(21, 75, 43)" }}>
        <div className="max-w-5xl mx-auto mt-6">
          <div className="mb-3 inline-flex items-center gap-2">
            <span className="h-[2px] w-5 rounded-full bg-[#4ade80]" />
            <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#4ade80]">
              {t.eyebrow}
            </span>
          </div>

          <h1
            className="text-white text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3"
            style={{ fontFamily: "'Playfair Display',serif" }}
          >
            <span className="text-orange-400">{t.titleHighlight}</span> {t.titleSuffix}
          </h1>

          <p className="text-white/70 text-sm leading-relaxed max-w-lg">
            {t.headerDesc1}
            <br />
            <span className="text-white/50 text-xs">{t.headerDesc2}</span>
          </p>
        </div>
      </div>

      {/* Form Container */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 lg:p-10">
            {/* Form Title & Icon */}
            <div className="flex items-center gap-4 mb-8 pb-6 border-b-2 border-green-100">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-xl shrink-0"
                style={{ background: "rgb(21, 75, 43)" }}
              >
                <MessageSquare size={22} className="text-white" />
              </div>
              <div>
                <h2
                  className="text-xl font-extrabold text-gray-900 font-[var(--ff-nepali)]"
                  style={{ fontFamily: "'Playfair Display',serif" }}
                >
                  {t.formTitle}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">{t.formSubtitle}</p>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                <span className="font-bold">⚠</span>
                <span>{error}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Full Name */}
              <Field label={t.fullName} icon={User}>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t.namePlaceholder}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 bg-white outline-none focus:border-green-800 focus:ring-2 focus:ring-green-800/10 transition-all"
                />
              </Field>

              {/* Email */}
              <Field label={t.email} icon={Mail}>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.emailPlaceholder}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 bg-white outline-none focus:border-green-800 focus:ring-2 focus:ring-green-800/10 transition-all"
                />
              </Field>

              {/* Phone */}
              <Field label={t.phone} icon={Phone}>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t.phonePlaceholder}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 bg-white outline-none focus:border-green-800 focus:ring-2 focus:ring-green-800/10 transition-all"
                />
              </Field>

              {/* Category / Sector Dropdown */}
              <Field label={t.categoryLabel} icon={Tag}>
                <div className="relative">
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm bg-white outline-none focus:border-green-800 focus:ring-2 focus:ring-green-800/10 transition-all appearance-none cursor-pointer"
                    style={{ color: formData.category ? "#1f2937" : "#9ca3af" }}
                  >
                    <option value="">{t.categoryPlaceholder}</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-xs">
                    ▾
                  </span>
                </div>
              </Field>

              {/* Subject (Optional) */}
              <Field label={t.subjectLabel} col2 icon={FileText}>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={t.subjectPlaceholder}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 bg-white outline-none focus:border-green-800 focus:ring-2 focus:ring-green-800/10 transition-all"
                />
              </Field>

              {/* Message */}
              <Field label={t.messageLabel} col2 icon={MessageSquare}>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder={t.messagePlaceholder}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 outline-none focus:border-green-800 focus:ring-2 focus:ring-green-800/10 transition-all resize-y"
                />
              </Field>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!canSubmit || loading}
              className="mt-8 w-full py-3.5 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-2 transition-all shadow-md duration-300"
              style={{
                background: canSubmit && !loading ? "rgb(21, 75, 43)" : "#d1d5db",
                cursor: canSubmit && !loading ? "pointer" : "not-allowed",
                boxShadow: canSubmit && !loading ? "0 4px 14px rgba(21,75,43,0.3)" : "none",
              }}
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  {t.submitting}
                </>
              ) : (
                <>
                  <Send size={16} />
                  {t.submit}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackForm;